import React from 'react';
import styled from 'styled-components';

import { useQuery } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';

import useConfig from 'app/config';
import GenericPage from 'app/ui/components/layouts/generic-page';

import AccessDenied from 'app/ui/components/molecules/access-denied';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import ResourceSections from 'app/ui/components/molecules/resource-sections';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import ResourceNavigation from 'app/ui/components/molecules/resource-navigation';
import MoreResourceContent from 'app/ui/components/molecules/more-resource-content';
import AcademyBanner from 'app/ui/components/molecules/academy-banner';
import ResourceCta from 'app/features/resources/cta/resource-cta';

import { ResourcePage as RESOURCE } from 'app/pages/resource/resource.gql';
import type { ResourcePageQuery, ResourcePageQueryVariables } from 'app/types/graphql';
import useAudienceAccess from 'app/hooks/use-audience-access';
import useServices from 'app/services';
import { useAppState } from 'src/state';

const BreadcrumbWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  display: none;

  ${({ theme: { media, spacing } }) =>
    `${media.medium(
      () => `
       margin: ${spacing(14)} 0 ${spacing(6)} 0;
       display: inline-flex;
       `,
    )}`}
`;

const ResourceSectionsWrapper = styled.div`
  ${({ theme: { spacing } }) => `
    padding-bottom: ${spacing(8)};
  `}
`;

type ResourcePageProps = {
  resourceId: string;
  userId: number;
  resourceListId?: string;
};

const ResourcePage: React.FC<ResourcePageProps> = ({ resourceId, userId, resourceListId }: ResourcePageProps) => {
  const { config } = useConfig();
  const services = useServices();
  const resourceListsEnabled = config.REACT_APP_RESOURCE_LISTS_ENABLED;

  const { data, loading, error } = useQuery<ResourcePageQuery, ResourcePageQueryVariables>(RESOURCE, {
    variables: { id: resourceId, userId },
  });
  const { checkAccess, isLoading: audienceLoading } = useAudienceAccess();

  if (loading || audienceLoading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  // A restricted resource is withheld by one-fiit-api (which returns null); on the frozen hyrox-api it
  // comes back in full, so the audience check is what withholds it. Either way the user sees a 403.
  //
  // Deliberate: an empty read also covers a nonexistent id and an unpublished draft, and all three
  // land on Access Denied. Both APIs collapse missing and unauthorised into the same null, so the
  // client cannot tell them apart anyway, and not distinguishing them keeps resource ids
  // non-enumerable. Change this only alongside an API that reports the two cases separately.
  if (!data?.resource) {
    return <AccessDenied />;
  }

  const verdict = checkAccess(data.resource.audiences);

  if (verdict === 'unavailable' || !data.user) {
    return <ErrorComponent />;
  }

  if (verdict === 'denied') {
    return <AccessDenied />;
  }

  const {
    resource: {
      title,
      content,
      image,
      sections = [],
      id,
      ctaLink,
      ctaLabel,
      ctaAppendPortalGymCodeQueryString,
      showAcademyPromo,
    },
    user: { gym, portalUserCode },
  } = data;

  const portalGymCode = gym?.portalGymCode;

  return (
    <>
      <GenericPage pageTitle={`Resource - ${title}`}>
        <ResourceHeader title={title} content={content} imageUrl={image?.url || image?.signedUrl} />
        <BreadcrumbWrapper>
          <StyledBreadcrumb contentTitle={title} />
          <ResourceCta
            label={ctaLabel}
            link={ctaLink}
            appendPortalGymCode={ctaAppendPortalGymCodeQueryString}
            portalGymCode={portalGymCode}
            portalUserCode={portalUserCode}
            actionHandlers={{
              'gym-update-link': services.hyrox365.createGymUpdateLink,
            }}
          />
        </BreadcrumbWrapper>
        <ResourceSectionsWrapper>
          <ResourceSections
            sections={sections || []}
            resourceId={id}
            actionHandlers={{
              'gym-update-link': services.hyrox365.createGymUpdateLink,
            }}
          />
        </ResourceSectionsWrapper>

        {resourceListsEnabled && resourceListId && (
          <ResourceNavigation resourceListId={resourceListId} currentResourceId={resourceId} />
        )}

        {resourceListsEnabled && <MoreResourceContent limit={3} />}
        {showAcademyPromo && <AcademyBanner />}
      </GenericPage>
    </>
  );
};

const Resource: React.FC = () => {
  const { id: resourceId } = useParams();
  const userId = useAppState(({ auth }) => auth.userId);
  const { search } = useLocation();

  const resourceListId = React.useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('resourceListId') || undefined;
  }, [search]);

  if (!resourceId || !userId) {
    return <ErrorComponent />;
  }

  return <ResourcePage resourceId={resourceId} resourceListId={resourceListId} userId={userId} />;
};

export default Resource;
